import type { BookingAnswerPath } from '@ascleo/frontdesk-contracts/booking-questionnaire/model'
import type { BookingRouteAvailability, BookingRuntimeRequest, BookingRuntimeResponse, BookingSubmissionRequest, BookingReviewRequest } from '@ascleo/frontdesk-contracts/booking-questionnaire/runtime'
import { docCirrusRpcClient } from './doc-cirrus.client'
import { readBookingRecoveryGeneration } from '../booking-submission-recovery'

/** Changed server offers require explicit patient reselection before showing new slots.
 * @since 1.1.8
 * @example new BookingOfferChanged({ kind: 'reception' });
 */
export class BookingOfferChanged extends Error {
  readonly route: BookingRouteAvailability
  constructor(route: BookingRouteAvailability) { super('Booking offer changed'); this.name = 'BookingOfferChanged'; this.route = route }
}

type Transport = Pick<typeof docCirrusRpcClient.frontdesk, 'beginBookingContext' | 'getBookingQuestionnaire' | 'getBookingRouteAvailability' | 'getQuestionnaireSlots' | 'getBookingReview' | 'submitQuestionnaireBooking' | 'revokeBookingContext'>

/** In-memory questionnaire session; answers and context never enter recovery storage.
 * @since 1.1.8
 * @example const client = createBookingQuestionnaireClient();
 */
export const createBookingQuestionnaireClient = (transport: Transport = docCirrusRpcClient.frontdesk, readGeneration: (patientId: string) => string = (patientId) => typeof localStorage === 'undefined' ? '' : readBookingRecoveryGeneration(localStorage, patientId)) => {
  let request: BookingRuntimeRequest | undefined
  let projection: BookingRuntimeResponse | undefined
  let offeredRoute: BookingRouteAvailability | undefined
  let offerGeneration = 0
  let generation = 0
  let recoveryGeneration: string | undefined
  const current = () => {
    if (request === undefined) throw new Error('Booking questionnaire session is not active')
    return request
  }
  const read = async (next: BookingRuntimeRequest) => {
    const ownedGeneration = ++generation
    request = next
    projection = undefined
    offeredRoute = undefined
    const result = await transport.getBookingQuestionnaire(next)
    if (ownedGeneration !== generation) throw new Error('Booking questionnaire request was superseded')
    projection = result
    return result
  }
  return {
    async begin(patientId: string, language: string) {
      const resetting = this.reset()
      const ownedGeneration = generation
      await resetting
      if (ownedGeneration !== generation) throw new Error('Booking questionnaire session was superseded')
      const capturedRecoveryGeneration = readGeneration(patientId)
      const context = await transport.beginBookingContext(patientId)
      if (ownedGeneration !== generation) {
        await transport.revokeBookingContext(context.contextHandle)
        throw new Error('Booking questionnaire session was superseded')
      }
      recoveryGeneration = capturedRecoveryGeneration
      return read({ contextHandle: context.contextHandle, patientId, language, path: [] })
    },
    async load(patientId: string, language: string) {
      if (request === undefined || request.patientId !== patientId) return this.begin(patientId, language)
      return read({ ...request, language })
    },
    async answer(answerId: string) {
      const active = current()
      if (projection?.kind !== 'question' || !projection.answers.some((answer) => answer.answerId === answerId)) throw new Error('Answer is not offered by the current question')
      return read({ ...active, path: [...active.path, { questionId: projection.questionId, answerId }] })
    },
    async back() {
      const active = current()
      if (active.path.length === 0) throw new Error('No earlier questionnaire answer')
      return read({ ...active, path: active.path.slice(0, -1) })
    },
    async changeLanguage(language: string) {
      return read({ ...current(), language })
    },
    async route() {
      if (projection?.kind !== 'booking') throw new Error('Questionnaire has not reached a booking route')
      const ownedGeneration = generation
      const ownedOffer = ++offerGeneration
      const result = await transport.getBookingRouteAvailability(current())
      if (ownedGeneration !== generation || ownedOffer !== offerGeneration) throw new Error('Booking questionnaire request was superseded')
      offeredRoute = result
      return result
    },
    async slots(appointmentTypeId: string, startDate: string, endDate: string) {
      if (projection?.kind !== 'booking') throw new Error('Questionnaire has not reached a booking route')
      const ownedGeneration = generation
      const ownedOffer = ++offerGeneration
      const result = await transport.getQuestionnaireSlots({ ...current(), appointmentTypeId, startDate, endDate })
      if (ownedGeneration !== generation || ownedOffer !== offerGeneration) throw new Error('Booking questionnaire request was superseded')
      const prior = offeredRoute
      const route = result.route
      offeredRoute = route
      if (prior === undefined || route.kind !== prior.kind || route.kind === 'reception' || !route.appointmentTypeIds.includes(appointmentTypeId)) throw new BookingOfferChanged(route)
      return result.slots
    },
    async review(binding: Omit<BookingReviewRequest, keyof BookingRuntimeRequest>) {
      if (projection?.kind !== 'booking' && projection?.kind !== 'disabled') throw new Error('Questionnaire has not reached confirmation')
      const ownedGeneration = generation
      const result = await transport.getBookingReview({ ...binding, ...current() })
      if (ownedGeneration !== generation) throw new Error('Booking questionnaire request was superseded')
      return result
    },
    async submit(binding: Omit<BookingSubmissionRequest, keyof BookingRuntimeRequest>) {
      if (projection?.kind !== 'booking' && projection?.kind !== 'disabled') throw new Error('Questionnaire has not reached confirmation')
      const ownedGeneration = generation
      const result = await transport.submitQuestionnaireBooking({ ...binding, ...current() })
      if (ownedGeneration !== generation) throw new Error('Booking questionnaire request was superseded')
      return result
    },
    /** Copy only transient semantic IDs for the final server-authoritative submission. */
    path(): BookingAnswerPath {
      return current().path.map((answer) => ({ ...answer }))
    },
    recoveryGeneration(): string {
      current()
      if (recoveryGeneration === undefined) throw new Error('Booking recovery generation is unavailable')
      return recoveryGeneration
    },
    async reset() {
      const previous = request
      ++generation
      request = undefined
      projection = undefined
      recoveryGeneration = undefined
      if (previous !== undefined) await transport.revokeBookingContext(previous.contextHandle)
    },
  }
}
